import * as React from "react";

import { components as reactSelectComponents } from "react-select";
import { useSelector } from "react-redux";

import { SelectField } from "components/Form/FormikControls";
import { ProtectedInput } from "components/SearchableSelector/ReactSelect";

import {
  selectCurrentDeploymentIds,
  selectDeploymentOptions,
  selectRoleDeploymentIds,
} from "selectors";

type DeploymentId = string | number;

interface DeploymentSelectFieldProps {
  disabled?: boolean;
  name: string;
  onChangeExtra?: ((value: unknown) => void) | null;
  label: string;
  required?: boolean;
  restrictToOwnDeployments?: boolean;
  retrictToSaleDeployments?: boolean; // TODO fix typo in prop name
  suggestedValueFieldName?: string;
  isMulti?: boolean;
  warningText?: string | null;
  existingDeploymentIds?: DeploymentId[];
  excludeDeploymentIds?: DeploymentId[];
}

export const DeploymentSelectField = ({
  disabled = false,
  name,
  onChangeExtra = undefined,
  label,
  required,
  restrictToOwnDeployments,
  retrictToSaleDeployments,
  suggestedValueFieldName = undefined,
  isMulti = false,
  warningText = null,
  existingDeploymentIds = undefined,
  excludeDeploymentIds = undefined,
}: DeploymentSelectFieldProps) => {
  let deploymentOptions = useSelector(selectDeploymentOptions);
  const roleDeploymentIds = useSelector(selectRoleDeploymentIds);
  const saleDeploymentIds = useSelector(selectCurrentDeploymentIds);

  if (restrictToOwnDeployments) {
    deploymentOptions = deploymentOptions.filter(deployment =>
      roleDeploymentIds.includes(deployment.value),
    );
  }

  if (retrictToSaleDeployments) {
    deploymentOptions = deploymentOptions.filter(deployment =>
      saleDeploymentIds.includes(deployment.value),
    );
  }

  if (excludeDeploymentIds) {
    deploymentOptions = deploymentOptions.filter(
      deployment => !excludeDeploymentIds.includes(deployment.value),
    );
  }

  const customComponents = React.useMemo(() => {
    if (!existingDeploymentIds) {
      return undefined;
    }
    return {
      ClearIndicator: () => null,
      Input: props => (
        <ProtectedInput {...props} lockedIds={existingDeploymentIds} />
      ),
      MultiValueRemove: props => {
        if (existingDeploymentIds.includes(props.data.value)) {
          return null;
        }
        return <reactSelectComponents.MultiValueRemove {...props} />;
      },
    };
  }, [existingDeploymentIds]);

  return (
    <SelectField
      disabled={disabled}
      label={label}
      name={name}
      onChangeExtra={onChangeExtra}
      options={deploymentOptions}
      required={required}
      isClearable={!required}
      isMulti={isMulti}
      warningText={warningText}
      suggestedValueFieldName={suggestedValueFieldName}
      components={customComponents}
    />
  );
};
